import React, { useState, useMemo, useEffect } from 'react';
import { Table, Pagination, Spinner, Form } from 'react-bootstrap';
import { FaCaretDown, FaCaretUp, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import ColorBullet from './ColorBullet';
import styles from './TableView.module.css';

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const TableView = ({ rows, columns, tableClass, isLoading, itemLabel = 'Items' }) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [sortConfig, setSortConfig] = useState({ by: null, direction: 'asc' });

	const sortedRows = useMemo(() => {
		let sortable = [...rows];
		if (sortConfig !== null) {
			sortable.sort((a, b) => {
				if (a[sortConfig.by] < b[sortConfig.by]) {
					return sortConfig.direction === 'asc' ? -1 : 1;
				}
				if (a[sortConfig.by] > b[sortConfig.by]) {
					return sortConfig.direction === 'asc' ? 1 : -1;
				}
				return 0;
			});
		}
		return sortable;
	}, [rows, sortConfig]);

	const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
	const indexOfLastRow = currentPage * rowsPerPage;
	const indexOfFirstRow = indexOfLastRow - rowsPerPage;
	const currentPageRows = sortedRows.slice(indexOfFirstRow, indexOfLastRow);

	useEffect(() => {
		// While a refresh is in flight the rows are emptied, which would collapse
		// totalPages to 1 and clamp the user back to the first page. Only clamp
		// once the real row count is known.
		if (isLoading) {
			return;
		}
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages, isLoading]);

	const handlePageChange = (page) => {
		setCurrentPage(Math.min(Math.max(1, page), totalPages));
	};

	const handlePerPageChange = (perPage) => {
		setRowsPerPage(perPage);
		setCurrentPage(1);
	};

	const requestSort = (by) => {
		let direction = 'asc';
		if (sortConfig.by === by && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ by, direction });
	};

	const getPageNumbers = () => {
		const pages = [];
		const maxVisible = 16;
		let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
		let end = start + maxVisible - 1;
		if (end > totalPages) {
			end = totalPages;
			start = Math.max(1, end - maxVisible + 1);
		}
		if (start > 1) {
			pages.push(
				<Pagination.Item key={1} onClick={() => handlePageChange(1)} activeLabel="">
					1
				</Pagination.Item>
			);
			if (start > 2) {
				pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
			}
		}
		for (let i = start; i <= end; i++) {
			pages.push(
				<Pagination.Item
					key={i}
					active={i === currentPage}
					onClick={() => handlePageChange(i)}
					activeLabel=""
				>
					{i}
				</Pagination.Item>
			);
		}
		if (end < totalPages) {
			if (end < totalPages - 1) {
				pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
			}
			pages.push(
				<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)} activeLabel="">
					{totalPages}
				</Pagination.Item>
			);
		}
		return pages;
	};

	const setSortIcon = (by) => {
		return sortConfig.by === by ? (sortConfig.direction === 'asc' ? <FaCaretUp /> : <FaCaretDown />) : null;
	};

	const renderFooter = () => (
		<div className={styles.tableFooter}>
			<div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
				<Pagination className={styles.pagination}>
					<Pagination.Prev
						disabled={currentPage === 1}
						onClick={() => handlePageChange(currentPage - 1)}
					/>
					{getPageNumbers()}
					<Pagination.Next
						disabled={currentPage === totalPages}
						onClick={() => handlePageChange(currentPage + 1)}
					/>
				</Pagination>
				<label htmlFor="tableViewPerPage" className={styles.perPageLabel}>Page Size:</label>
				<Form.Control
					as="select"
					id="tableViewPerPage"
					value={rowsPerPage}
					onChange={(e) => handlePerPageChange(parseInt(e.target.value, 10))}
					className={styles.perPage}
					style={{ width: 80 }}
				>
					{PER_PAGE_OPTIONS.map((size) => (
						<option key={size} value={size}>{size}</option>
					))}
				</Form.Control>
				<span className={styles.results}>
					Showing {currentPageRows.length} of {sortedRows.length} {itemLabel}
				</span>
			</div>
		</div>
	);

	return (
		isLoading ? (
			<div className="labels-container-div justify-content-center">
				<Spinner as="span" animation="border" size="md" role="status" aria-hidden="true" />
			</div>
		) :
			sortedRows.length === 0 ? (
				<div className="labels-container-div justify-content-center">No Items Found</div>
			) : (
				<>
					<Table className={tableClass} hover>
						<thead>
							<tr>
								{
									columns && columns.map((column, index) => {
										return column.key !== '__actions__' ? (
											<th key={index} className='cursor-pointer' onClick={() => requestSort(column.key)}>{column.label}{setSortIcon(column.key)}</th>
										) : (
											<th key={index}>{column.label}</th>
										)
									})
								}
							</tr>
						</thead>
						<tbody>
							{
								currentPageRows.map((row, row_index) => (
									<tr key={row_index}>
										{
											columns?.map((column, index) => {
												let content, className;
												if (column.key === '__actions__') {
													content = row[column.key]();
													className = 'actions-column';
												} else if (column.key === 'color') {
													content = <ColorBullet color={row[column.key]} />;
												} else if (column?.mode === 'badge') {
													content = <ColorBullet color={row[column.key]['color']} text={row[column.key]['name']} />;
												} else if (column?.mode === 'badges') {
													content = row[column.key].map(label => <ColorBullet color={label['color']} text={label['name']} />);
												} else if (column?.mode === 'check') {
													content = row[column.key]
														?
														<FaCheckCircle className='text-success' />
														:
														<FaExclamationCircle className='text-danger' />
												} else {
													content = row[column.key];
												}
												return (
													<td key={column.key + index} className={className}>
														{content}
													</td>
												);
											})
										}
									</tr>
								))
							}
						</tbody>
					</Table>
					{renderFooter()}
				</>
			)
	)
}

export default TableView
